import { useState, useMemo } from 'react';
import { useConfirm } from 'material-ui-confirm';
import { toast } from 'react-toastify';

// Use unified date utils
import { moment } from '../../utils/dateUtils';

import { Box, Typography, Link, Tooltip, CircularProgress, Backdrop } from '@mui/material';

// Components
import TooltipTitle from './Calendar/TooltipTitle';

// Utils
import messages from '../../static/messages';

// Services
import appointmentsServices from '../../services/appointmentsServices';

// Styles
import { links_styles } from './Calendar/styles';

const CancelledWeekView = ({ date, appointments, technicians, refreshAppointments, queue }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  
  const confirm = useConfirm();
  moment.tz.setDefault('America/Los_Angeles');

  // Get week days
  const weekDays = useMemo(() => {
    const startOfWeek = moment(date).startOf('week');
    return Array.from({ length: 7 }, (_, i) => 
      moment(startOfWeek).add(i, 'days')
    );
  }, [date]);

  // Group appointments by day and technician - CLEAN SOLUTION using only lastAssignedUser
  const appointmentsByDayAndTech = useMemo(() => {
    const grouped = {};
    
    weekDays.forEach(day => {
      const dayKey = day.format('YYYY-MM-DD');
      grouped[dayKey] = {};
      
      technicians?.forEach(tech => {
        grouped[dayKey][tech.tech_id || 'unassigned'] = [];
      });
    });

    appointments?.forEach(apt => {
      if (!apt.startTime) return;
      
      const dayKey = moment(apt.startTime).format('YYYY-MM-DD');
      // Use lastAssignedUser ONLY
      const techId = apt.lastAssignedUser?.id || 'unassigned';
      
      if (grouped[dayKey] && grouped[dayKey][techId]) {
        grouped[dayKey][techId].push(apt);
      }
    });

    return grouped;
  }, [appointments, technicians, weekDays]);

  // Drag handlers
  const dragStartHandler = (e, item) => {
    setCurrentItem(item);
    e.target.style.opacity = '0.5';
  };

  const dragEndHandler = (e) => {
    e.target.style.opacity = '1';
    setCurrentItem(null);
  };

  const dragOverHandler = (e) => {
    e.preventDefault();
    e.target.style.boxShadow = 'inset 0 0 20px 0 #ccc';
  };

  const dragLeaveHandler = (e) => {
    e.target.style.boxShadow = 'none';
  };

  const dropToQueueHandler = async (e) => {
    e.preventDefault();
    e.target.style.boxShadow = 'none';
    
    if (!currentItem || isLoading) return;

    confirm({
      title: 'Restore Appointment',
      description: `Are you sure you want to restore this appointment to the queue?`,
    })
      .then(async () => {
        setIsLoading(true);
        try {
          const response = await appointmentsServices.restoreToQueue(currentItem.id);
          if (response.status === 200) {
            toast.success('Appointment restored to queue');
            refreshAppointments();
          }
        } catch (error) {
          toast.error(messages.errors.error_try_again);
        } finally {
          setIsLoading(false);
          setCurrentItem(null);
        }
      })
      .catch(() => setCurrentItem(null));
  };

  // Calculate widths for 1080p
  const techColumnWidth = 100;
  const dayColumnWidth = Math.floor((1920 - techColumnWidth - 50) / 7);

  return (
    <Box>
      <Backdrop open={isLoading} sx={{ zIndex: 9999, color: '#fff' }}>
        <CircularProgress color="inherit" size={60} />
      </Backdrop>

      {/* Queue Drop Zone - compact */}
      <Box sx={{ mb: 1 }}>
        <Box
          sx={{
            height: 40,
            border: '2px dashed #4CAF50',
            backgroundColor: '#E8F5E9',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 1,
          }}
          onDragOver={dragOverHandler}
          onDragLeave={dragLeaveHandler}
          onDrop={dropToQueueHandler}
        >
          <Typography variant="body2" sx={{ color: '#2E7D32', fontWeight: 'bold', fontSize: 12 }}>
            ↓ QUEUE - Drop to restore ({queue?.length || 0}) ↓
          </Typography>
        </Box>
      </Box>

      {/* Week Grid - compact for 1080p */}
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        {/* Technician column */}
        <Box sx={{ width: techColumnWidth, flexShrink: 0 }}>
          <Box sx={{ height: 40, p: 0.5, fontWeight: 'bold', backgroundColor: '#f5f5f5', fontSize: 11 }}>
            Technician
          </Box>
          {technicians?.map((tech, idx) => (
            <Box 
              key={tech.tech_id || idx}
              sx={{ 
                p: 0.5, 
                backgroundColor: tech.color || '#808080',
                color: '#fff',
                fontWeight: 'bold',
                fontSize: 10,
                height: 50,
                display: 'flex',
                alignItems: 'center',
                overflow: 'hidden',
                mb: 0.25,
              }}
            >
              <Typography variant="caption" sx={{ 
                fontSize: 10, 
                whiteSpace: 'nowrap', 
                overflow: 'hidden', 
                textOverflow: 'ellipsis' 
              }}>
                {tech.name}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Day columns */}
        {weekDays.map((day, dayIdx) => (
          <Box key={dayIdx} sx={{ width: dayColumnWidth, flexShrink: 0 }}>
            {/* Day header */}
            <Box sx={{ 
              height: 40,
              p: 0.5, 
              textAlign: 'center', 
              fontWeight: 'bold',
              backgroundColor: day.isSame(moment(), 'day') ? '#fff3e0' : '#f5f5f5',
            }}>
              <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: 11, display: 'block' }}>
                {day.format('ddd')}
              </Typography>
              <Typography variant="caption" sx={{ fontSize: 10 }}>
                {day.format('MM/DD')}
              </Typography>
            </Box>

            {/* Technician rows for this day */}
            {technicians?.map((tech, techIdx) => {
              const dayKey = day.format('YYYY-MM-DD');
              const techId = tech.tech_id || 'unassigned';
              const dayAppointments = appointmentsByDayAndTech[dayKey]?.[techId] || [];

              return (
                <Box 
                  key={techIdx}
                  sx={{ 
                    p: 0.25, 
                    backgroundColor: '#fafafa',
                    border: '1px solid #e0e0e0',
                    height: 50,
                    overflowY: 'auto',
                    mb: 0.25,
                  }}
                >
                  {dayAppointments.map((apt, aptIdx) => (
                    <Tooltip
                      key={apt.id || aptIdx}
                      title={<TooltipTitle info={apt} />}
                      placement="top"
                      arrow
                    >
                      <Box
                        sx={{
                          backgroundColor: tech.color || '#808080',
                          opacity: 0.85,
                          p: 0.25,
                          mb: 0.25,
                          borderRadius: 0.5,
                          cursor: 'grab',
                          '&:hover': { opacity: 1 },
                        }}
                        draggable
                        onDragStart={(e) => dragStartHandler(e, apt)}
                        onDragEnd={dragEndHandler}
                      >
                        <Link href={`/appointments/${apt.id}`} underline="none" sx={links_styles}>
                          <Typography variant="caption" sx={{ color: '#fff', fontWeight: 'bold', fontSize: 9 }}>
                            {apt.property?.zipCode || '?'}
                          </Typography>
                        </Link>
                      </Box>
                    </Tooltip>
                  ))}
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default CancelledWeekView;
